import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck, CheckCircle2, Download, ArrowRight, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BACKEND}/api/public` });
const imgUrl = (u) => (!u ? "" : String(u).startsWith("/api/") ? `${BACKEND}${u}` : u);
const C = { ink: "#1A1A1A", sub: "#52525B", mute: "#A1A1AA", line: "#E4E4E7", bg: "#F9F8F6", surface: "#FFFFFF", navy: "#121B28" };
const money = (n, cur = "EUR") => new Intl.NumberFormat("en-IE", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(Number(n) || 0);

const Fonts = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Manrope:wght@300;400;500;600;700&display=swap');
    .et-serif{font-family:'Cormorant Garamond',Georgia,serif;}
    .et-sans{font-family:'Manrope',system-ui,sans-serif;}
  `}</style>
);

export default function PayExtra() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const paypalFlag = sp.get("paypal");
  const orderId = sp.get("token");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [paid, setPaid] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
  const [tos, setTos] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    pub.get(`/pay/extra/${token}`).then(({ data }) => setInfo(data))
      .catch((e) => setError(e?.response?.data?.detail || "We could not load this payment link."))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (paypalFlag !== "return" || !orderId) return;
    setCapturing(true);
    pub.post(`/pay/extra/${token}/capture/${orderId}`).then(({ data }) => {
      if (data.status === "COMPLETED") setPaid({ amount: data.amount, currency: data.currency, receiptUrl: data.receipt?.url ? `${BACKEND}${data.receipt.url}` : null });
      else setError("We could not confirm your payment. Please try again or contact us.");
    }).catch(() => setError("Something went wrong while finishing your payment.")).finally(() => setCapturing(false));
  }, [paypalFlag, orderId, token]);

  const ok = form.first_name.trim() && form.last_name.trim() && /\S+@\S+\.\S+/.test(form.email) && tos && !submitting;
  const submit = async () => {
    if (!ok) return;
    setSubmitting(true);
    try {
      const { data } = await pub.post(`/pay/extra/${token}/create-order`, { ...form, tos_accepted: tos });
      window.location.href = data.approve_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start the payment.");
      setSubmitting(false);
    }
  };

  const wl = info?.whitelabel_enabled ? info.whitelabel : null;
  const brand = wl?.agency_name || "Espíritu Travel";
  const success = !!paid || info?.status === "paid";

  return (
    <div className="min-h-screen" style={{ backgroundColor: C.bg, color: C.ink }} data-testid="pay-extra-page">
      <Fonts />
      <header className="border-b" style={{ borderColor: C.line, backgroundColor: C.surface }}>
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          {wl?.logo_url ? <img src={imgUrl(wl.logo_url)} alt={brand} className="h-8 object-contain" /> : <img src="/brand/et-logo-navy.png" alt="Espíritu Travel" className="h-8 object-contain" />}
          <span className="et-sans flex items-center gap-1.5 text-xs" style={{ color: C.sub }}><ShieldCheck className="h-4 w-4" /> Secure payment via PayPal</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10">
        {loading && <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin" style={{ color: C.mute }} /></div>}
        {!loading && error && !info && (
          <div className="border bg-white p-8 text-center" style={{ borderColor: C.line }} data-testid="pay-extra-error"><AlertTriangle className="mx-auto h-8 w-8 text-amber-600" /><p className="et-sans mt-3 text-sm" style={{ color: C.sub }}>{error}</p></div>
        )}
        {info && (
          <>
            {info.hero_image && <img src={imgUrl(info.hero_image)} alt="" className="mb-8 h-48 w-full object-cover" />}
            <p className="et-sans text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: C.mute }}>{info.trip_title}</p>
            <h1 className="et-serif mt-2 text-4xl font-medium md:text-5xl" data-testid="pay-extra-title">{info.title}</h1>
            {info.description && <p className="et-sans mt-3 text-base font-light" style={{ color: C.sub }}>{info.description}</p>}

            {capturing && <div className="mt-8 flex items-center gap-3 border bg-white p-6" style={{ borderColor: C.line }}><Loader2 className="h-5 w-5 animate-spin" /><span className="et-sans text-sm">Confirming your payment…</span></div>}

            {!capturing && success && (
              <div className="mt-8 border bg-white p-8 text-center" style={{ borderColor: C.line }} data-testid="pay-extra-success">
                <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
                <h2 className="et-serif mt-4 text-3xl font-medium">Thank you!</h2>
                <p className="et-sans mt-2 text-sm font-light" style={{ color: C.sub }}>We've received your payment{paid ? ` of ${money(paid.amount, paid.currency)}` : ""} for “{info.title}”.</p>
                {paid?.receiptUrl && <a href={paid.receiptUrl} target="_blank" rel="noreferrer" className="et-sans mx-auto mt-6 inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white" style={{ backgroundColor: C.navy }} data-testid="pay-extra-receipt"><Download className="h-4 w-4" /> Download receipt</a>}
              </div>
            )}

            {!capturing && !success && (
              <div className="mt-8 space-y-6">
                {error && <p className="et-sans border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="pay-extra-error">{error}</p>}
                {paypalFlag === "cancel" && <p className="et-sans border p-3 text-sm" style={{ borderColor: C.line, color: C.sub }}>Your payment was cancelled. You can try again below.</p>}
                <div className="flex items-end justify-between border bg-white p-6" style={{ borderColor: C.line }}>
                  <div>
                    <p className="et-sans text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: C.mute }}>Amount due</p>
                    <p className="et-serif mt-1 text-5xl font-medium" data-testid="pay-extra-amount">{money(info.amount, info.currency)}</p>
                  </div>
                  <p className="et-sans text-right text-xs font-light" style={{ color: C.sub }}>
                    {info.currency === "USD" ? `EUR ${info.amount_eur} at 1 EUR = ${Number(info.fx_rate).toFixed(4)} USD` : ""}
                    {info.fee_pct ? <><br />Includes {info.fee_pct}% processing fee</> : null}
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div><Label className="et-sans text-xs" style={{ color: C.sub }}>First name *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1 rounded-none" data-testid="pay-extra-first" /></div>
                  <div><Label className="et-sans text-xs" style={{ color: C.sub }}>Last name *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1 rounded-none" data-testid="pay-extra-last" /></div>
                  <div className="sm:col-span-2"><Label className="et-sans text-xs" style={{ color: C.sub }}>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-none" data-testid="pay-extra-email" /></div>
                </div>
                <label className="flex cursor-pointer items-start gap-3 border p-4 bg-white" style={{ borderColor: tos ? C.navy : C.line }}>
                  <input type="checkbox" checked={tos} onChange={(e) => setTos(e.target.checked)} className="mt-1 h-4 w-4" data-testid="pay-extra-tos" />
                  <span className="et-sans text-sm font-light" style={{ color: C.sub }}>I accept the <a href="https://www.espiritutravel.com/terms" target="_blank" rel="noreferrer" className="underline" style={{ color: C.ink }}>Terms & Conditions</a> of {brand}. Your acceptance is recorded with date and IP address.</span>
                </label>
                <button type="button" disabled={!ok} onClick={submit} className="et-sans flex w-full items-center justify-center gap-2 px-6 py-4 text-base font-semibold text-white disabled:opacity-50" style={{ backgroundColor: C.navy }} data-testid="pay-extra-submit">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Pay {money(info.amount, info.currency)} with PayPal <ArrowRight className="h-4 w-4" /></>}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
